import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companiesApi } from '@/lib/companies';
import { scimApi, ScimTokenSummary } from '@/lib/scim';
import { AppShell } from '@/components/app/AppShell';
import { AdminSiblingNav } from '@/components/app/AdminSiblingNav';
import { ConfirmDialog, InlineAlert } from '@/components/app/Modal';
import { useFormatDateTime } from '@/lib/datetime';

export function ScimAdminPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companiesApi.list(),
  });
  const companyId = companies[0]?.id;

  const { data: tokens = [], isLoading } = useQuery({
    queryKey: ['scim-tokens', companyId],
    queryFn: () => scimApi.list(companyId!),
    enabled: !!companyId,
  });

  const [displayName, setDisplayName] = useState('');
  const [revealed, setRevealed] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ScimTokenSummary | null>(null);

  const mint = useMutation({
    mutationFn: () => scimApi.mint(companyId!, displayName),
    onSuccess: (r) => {
      setRevealed(r.token);
      setDisplayName('');
      qc.invalidateQueries({ queryKey: ['scim-tokens', companyId] });
    },
  });

  const revoke = useMutation({
    mutationFn: (tokenId: string) => scimApi.revoke(companyId!, tokenId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scim-tokens', companyId] });
      setRevokeTarget(null);
    },
  });

  return (
    <AppShell
      title={t('scim.title', { defaultValue: 'SCIM provisioning' })}
      description={t('scim.subtitle', { defaultValue: 'Sync users from your IdP via SCIM 2.0.' }) as string}
      maxWidth="max-w-4xl"
    >
      <AdminSiblingNav wsId={wsId!} current="scim" />
      <div className="space-y-5">
        <div className="card-paper px-4 py-3 text-sm text-ink-700">
          <span className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold">
            {t('scim.endpoint')}
          </span>
          <div className="num font-semibold mt-1">{scimApi.endpointUrl()}</div>
        </div>

        <section className="card-paper p-5">
          <h2 className="display text-[15px] font-semibold text-ink-900">{t('scim.mint')}</h2>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="block flex-1 min-w-[200px]">
              <span className="block text-[11px] uppercase tracking-[0.12em] text-ink-500 mb-1 font-semibold">
                {t('scim.token_name')}
              </span>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t('scim.token_name_placeholder') as string}
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
              />
            </label>
            <button
              onClick={() => mint.mutate()}
              disabled={mint.isPending || !displayName.trim() || !companyId}
              className="btn-press px-4 py-2 text-sm"
            >
              {mint.isPending ? t('scim.minting') : t('scim.mint')}
            </button>
          </div>

          {revealed && (
            <div className="mt-4 rounded-xl bg-emerald-50/70 border border-emerald-200 p-4">
              <p className="text-xs text-emerald-800 font-semibold mb-2 uppercase tracking-[0.12em]">
                {t('scim.token_revealed')}
              </p>
              <code className="num block break-all bg-white px-3 py-2 rounded border border-emerald-200 text-xs">
                {revealed}
              </code>
              <div className="mt-2 flex gap-3">
                <button
                  onClick={() => { void navigator.clipboard?.writeText(revealed); }}
                  className="text-xs text-accent-600 hover:underline"
                >
                  {t('scim.token_copy')}
                </button>
                <button
                  onClick={() => setRevealed(null)}
                  className="text-xs text-ink-500 hover:text-ink-900"
                >
                  {t('common.dismiss', { defaultValue: 'Dismiss' })}
                </button>
              </div>
            </div>
          )}

          {mint.error && (
            <div className="mt-3">
              <InlineAlert tone="error">{(mint.error as Error).message}</InlineAlert>
            </div>
          )}
        </section>

        <section className="card-paper p-5">
          <h2 className="display text-[15px] font-semibold text-ink-900">{t('scim.tokens')}</h2>
          {isLoading ? (
            <div className="mt-3 text-sm text-ink-500">{t('common.loading')}</div>
          ) : tokens.length === 0 ? (
            <p className="mt-3 text-sm text-ink-500">{t('scim.empty')}</p>
          ) : (
            <ul className="mt-3 divide-y divide-ink-100">
              {tokens.map((tok) => (
                <TokenRow key={tok.id} token={tok} onRevoke={() => setRevokeTarget(tok)} t={t} />
              ))}
            </ul>
          )}
        </section>
      </div>

      <ConfirmDialog
        open={!!revokeTarget}
        onOpenChange={(next) => { if (!next) setRevokeTarget(null); }}
        title={t('scim.revoke_title', { defaultValue: 'Revoke this token?' })}
        description={
          revokeTarget
            ? (t('scim.revoke_body', {
                defaultValue: 'Revoking "{name}" will immediately disable any SCIM client using it. This cannot be undone.',
                name: revokeTarget.displayName,
              }) as string)
            : undefined
        }
        confirmLabel={t('scim.revoke', { defaultValue: 'Revoke' }) as string}
        onConfirm={() => { if (revokeTarget) revoke.mutate(revokeTarget.id); }}
        pending={revoke.isPending}
      />
    </AppShell>
  );
}

function TokenRow({ token, onRevoke, t }: { token: ScimTokenSummary; onRevoke: () => void; t: (k: string) => string }) {
  const fmt = useFormatDateTime();
  return (
    <li className="py-3 flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-ink-900">{token.displayName}</p>
        <p className="text-xs num text-ink-500">
          {fmt(token.createdAt)} · {token.id.slice(0, 8)}
        </p>
      </div>
      <button onClick={onRevoke} className="text-xs text-warn-600 hover:underline">
        {t('scim.revoke')}
      </button>
    </li>
  );
}
